"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { NavGroup, NavItem, NavSpec } from "./types";

/**
 * 側欄（2026-09-02 視覺翻新）
 *
 * 改版前的問題：摺疊邏輯只存在於「項目」層（NavItemView 的 open state），
 * 「群組」層完全沒有摺疊機制 —— 原本 `group.label && !collapsed` 只控制群組
 * 標題文字要不要顯示，並不控制 group.items 要不要渲染。因此 17 個群組永遠
 * 全展開，側欄實測高達 3,700px，導覽實質不可用。
 *
 * 本次改為手風琴：預設只展開「目前所在」的群組，其餘收合成單行（40px），
 * 整份選單約 680px、一屏可見。另補上選單搜尋與常用釘選。
 *
 * ⚠️ 詞條一字未改。搜尋高亮只是在渲染時把命中片段包一層 <mark>，
 *    item.label 的字串本身沒有被改動 —— 驗證迴圈以「與舊站詞條一字不差」
 *    為比對基準，這點必須守住。
 */

const PIN_STORAGE_KEY = "ccheng_admin_nav_pins";
const MAX_PINS = 8;

export interface SidebarProps {
  nav: NavSpec;
  activeHref?: string;
  collapsed?: boolean;
  brand?: React.ReactNode;
  /** 未提供則預設用 <a href>；App Router 專案可傳入 Next 的 <Link> 包裝。 */
  linkComponent?: React.ComponentType<{ href: string; className: string; children: React.ReactNode }>;
  /** 關閉選單搜尋框（預設開啟）。 */
  disableSearch?: boolean;
  /** 關閉常用釘選區（預設開啟）。 */
  disablePins?: boolean;
}

type LinkComp = React.ComponentType<{ href: string; className: string; children: React.ReactNode }>;

function DefaultLink({
  href,
  className,
  children,
  ...rest
}: {
  href: string;
  className: string;
  children: React.ReactNode;
} & Record<string, unknown>) {
  return (
    <a href={href} className={className} {...rest}>
      {children}
    </a>
  );
}

/** 依關鍵字把 label 切成片段並把命中處包 <mark>；不改動原字串。 */
function highlight(label: string, query: string): React.ReactNode {
  if (!query) return label;
  const lower = label.toLowerCase();
  const q = query.toLowerCase();
  const parts: React.ReactNode[] = [];
  let from = 0;
  let at = lower.indexOf(q);
  let n = 0;
  while (at !== -1) {
    if (at > from) parts.push(label.slice(from, at));
    parts.push(
      <mark key={`m${n++}`} className="cui-nav-mark">
        {label.slice(at, at + q.length)}
      </mark>,
    );
    from = at + q.length;
    at = lower.indexOf(q, from);
  }
  if (from < label.length) parts.push(label.slice(from));
  return parts;
}

/** 遞迴判斷這個項目（或其子項）是否命中關鍵字。 */
function itemMatches(item: NavItem, q: string): boolean {
  if (item.label.toLowerCase().includes(q)) return true;
  return (item.children ?? []).some((c) => itemMatches(c, q));
}

/** 只保留命中的項目（父項命中則整支保留）。 */
function filterItems(items: NavItem[], q: string): NavItem[] {
  const out: NavItem[] = [];
  for (const item of items) {
    if (item.label.toLowerCase().includes(q)) {
      out.push(item);
      continue;
    }
    const kids = filterItems(item.children ?? [], q);
    if (kids.length) out.push({ ...item, children: kids });
  }
  return out;
}

function countLeaves(items: NavItem[]): number {
  return items.reduce((n, it) => n + (it.children?.length ? countLeaves(it.children) : 1), 0);
}

/** 攤平成 href → label 的對照，供釘選區反查詞條。 */
function flattenByHref(nav: NavSpec): Map<string, string> {
  const map = new Map<string, string>();
  const walk = (items: NavItem[]) => {
    for (const it of items) {
      if (it.href) map.set(it.href, it.label);
      if (it.children?.length) walk(it.children);
    }
  };
  for (const g of nav) walk(g.items);
  return map;
}

function groupContainsHref(group: NavGroup, href?: string): boolean {
  if (!href) return false;
  const walk = (items: NavItem[]): boolean =>
    items.some((it) => it.href === href || (it.children?.length ? walk(it.children) : false));
  return walk(group.items);
}

function NavItemView({
  item,
  activeHref,
  depth,
  Link,
  query,
  pinned,
  onTogglePin,
}: {
  item: NavItem;
  activeHref?: string;
  depth: number;
  Link: LinkComp;
  query: string;
  pinned: Set<string>;
  onTogglePin?: (href: string) => void;
}) {
  const hasChildren = !!item.children?.length;
  const isActive = item.href !== undefined && item.href === activeHref;
  // 搜尋中一律展開，讓命中的子項直接看得到
  const [open, setOpen] = useState(
    () => hasChildren && !!activeHref && item.children!.some((c) => c.href === activeHref),
  );
  const expanded = query ? true : open;

  const label = (
    <>
      {item.icon}
      <span>{highlight(item.label, query)}</span>
      {hasChildren && <span className="cui-nav-toggle">{expanded ? "▾" : "▸"}</span>}
    </>
  );

  return (
    <div>
      {item.href && !hasChildren ? (
        /*
         * 釘選鈕必須是連結的**兄弟節點**，不能包在 <a> 裡面：
         *  (1) 巢狀在連結內的 role="button" 是無效 ARIA（互動元素不可互相巢狀）；
         *  (2) 它的 aria-label 會被算進連結的無障礙名稱，讓「常見問答」變成
         *      「常見問答 釘選到常用」——螢幕閱讀器唸錯，也會讓以詞條比對的
         *      驗證迴圈誤判成詞條被改。
         */
        <div className="cui-nav-item-row">
          {/*
           * 搜尋高亮會把 label 切成多個節點（<mark> 包住命中片段），而無障礙名稱的
           * 計算會在元素邊界插入空白 ——「權限管理」會被唸成「權限 管理」。
           * 用 aria-label 鎖死原詞條，確保 accessibility tree 裡也是一字不差。
           */}
          <Link
            href={item.href}
            className={`cui-nav-item${isActive ? " cui-active" : ""}`}
            {...(query ? { "aria-label": item.label } : {})}
          >
            {label}
          </Link>
          {onTogglePin && (
            <button
              type="button"
              aria-label={`${pinned.has(item.href) ? "取消釘選" : "釘選到常用"}：${item.label}`}
              className={`cui-nav-pin-btn${pinned.has(item.href) ? " cui-pinned" : ""}`}
              onClick={() => onTogglePin(item.href!)}
            >
              {pinned.has(item.href) ? "★" : "☆"}
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className={`cui-nav-item${isActive ? " cui-active" : ""}`}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={hasChildren ? expanded : undefined}
        >
          {label}
        </button>
      )}
      {hasChildren && expanded && (
        <div className="cui-nav-children">
          {item.children!.map((child) => (
            <NavItemView
              key={child.key}
              item={child}
              activeHref={activeHref}
              depth={depth + 1}
              Link={Link}
              query={query}
              pinned={pinned}
              onTogglePin={onTogglePin}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({
  nav,
  activeHref,
  collapsed,
  brand,
  linkComponent,
  disableSearch,
  disablePins,
}: SidebarProps) {
  const Link = linkComponent ?? DefaultLink;
  const [query, setQuery] = useState("");
  const [pins, setPins] = useState<string[]>([]);
  // 手風琴：同一時間只展開一個群組。初值＝目前所在的群組。
  const [openKey, setOpenKey] = useState<string | null>(
    () => nav.find((g) => groupContainsHref(g, activeHref))?.key ?? null,
  );
  // 收合態下 hover 哪個群組（決定 flyout 顯示哪一組）
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  // 換頁時把所在群組帶到展開狀態（使用者手動切換過的仍以本地 state 為準，
  // 因為 activeHref 變動才會觸發）
  useEffect(() => {
    const g = nav.find((grp) => groupContainsHref(grp, activeHref));
    if (g) setOpenKey(g.key);
  }, [activeHref, nav]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(PIN_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setPins(parsed.filter((x): x is string => typeof x === "string"));
      }
    } catch {
      /* 隱私模式／已清除站台資料：釘選區留空即可，不影響選單本身 */
    }
  }, []);

  const togglePin = useCallback((href: string) => {
    setPins((prev) => {
      const next = prev.includes(href) ? prev.filter((h) => h !== href) : [href, ...prev].slice(0, MAX_PINS);
      try {
        window.localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* 寫入失敗（無痕視窗等）不影響本次操作 */
      }
      return next;
    });
  }, []);

  const hrefLabels = useMemo(() => flattenByHref(nav), [nav]);
  const pinnedSet = useMemo(() => new Set(pins), [pins]);
  const q = query.trim().toLowerCase();

  // 搜尋中：只留下命中的群組與項目
  const shownGroups = useMemo(() => {
    if (!q) return nav;
    const out: NavGroup[] = [];
    for (const g of nav) {
      const items = filterItems(g.items, q);
      if (items.length) out.push({ ...g, items });
    }
    return out;
  }, [nav, q]);

  const hitCount = useMemo(() => (q ? shownGroups.reduce((n, g) => n + countLeaves(g.items), 0) : 0), [q, shownGroups]);

  const showSearch = !disableSearch && !collapsed;
  const showPins = !disablePins && !collapsed && !q && pins.length > 0;

  return (
    <aside className={`cui-shell-sidebar${collapsed ? " cui-collapsed" : ""}`}>
      {brand !== undefined && <div className="cui-shell-brand">{brand}</div>}

      {showSearch && (
        <div className="cui-nav-search-wrap">
          <span className="cui-nav-search-icon" aria-hidden>
            🔍
          </span>
          <input
            className="cui-nav-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜尋選單…"
            aria-label="搜尋選單"
          />
        </div>
      )}

      <nav className="cui-shell-nav" aria-label="主選單">
        {showPins && (
          <>
            <div className="cui-nav-section">☆ 常用</div>
            {pins.map((href) => {
              const label = hrefLabels.get(href);
              if (!label) return null;
              return (
                // 同上：取消釘選鈕是連結的兄弟節點，不污染連結的無障礙名稱
                <div key={href} className="cui-nav-item-row">
                  <Link href={href} className={`cui-nav-pin${href === activeHref ? " cui-active" : ""}`}>
                    <span className="cui-nav-pin-star" aria-hidden>
                      ★
                    </span>
                    <span>{label}</span>
                  </Link>
                  <button
                    type="button"
                    aria-label={`取消釘選：${label}`}
                    className="cui-nav-pin-btn cui-pinned"
                    onClick={() => togglePin(href)}
                  >
                    ★
                  </button>
                </div>
              );
            })}
            <div className="cui-nav-section">全部功能</div>
          </>
        )}

        {q && <div className="cui-nav-search-hits">{hitCount ? `找到 ${hitCount} 個項目` : "找不到符合的項目"}</div>}

        {shownGroups.map((group) => {
          // 搜尋中一律展開命中的群組；否則依手風琴狀態
          const isOpen = q ? true : openKey === group.key;
          return (
            <div
              key={group.key}
              className={`cui-nav-group${isOpen ? " cui-open" : ""}`}
              onMouseEnter={collapsed ? () => setHoverKey(group.key) : undefined}
              onMouseLeave={collapsed ? () => setHoverKey(null) : undefined}
            >
              {group.label !== undefined && (
                <button
                  type="button"
                  className="cui-nav-group-header"
                  aria-expanded={isOpen}
                  title={collapsed ? group.label : undefined}
                  onClick={() => setOpenKey((k) => (k === group.key ? null : group.key))}
                >
                  {group.icon !== undefined && (
                    <span className="cui-nav-group-icon" aria-hidden>
                      {group.icon}
                    </span>
                  )}
                  <span className="cui-nav-group-label">{group.label}</span>
                  <span className="cui-nav-group-caret" aria-hidden>
                    ▶
                  </span>
                </button>
              )}

              {/* 收合態：hover 時該群組完整清單向右浮出，不必展開整個側欄 */}
              {collapsed && hoverKey === group.key && (
                <div className="cui-nav-flyout">
                  {group.label !== undefined && <div className="cui-nav-flyout-title">{group.label}</div>}
                  {group.items.map((item) => (
                    <NavItemView
                      key={item.key}
                      item={item}
                      activeHref={activeHref}
                      depth={0}
                      Link={Link}
                      query=""
                      pinned={pinnedSet}
                    />
                  ))}
                </div>
              )}

              {/* 沒有 label 的群組沒有摺疊觸發點，一律展開（否則會永久藏起來） */}
              {!collapsed && (isOpen || group.label === undefined) && (
                <div className="cui-nav-group-items">
                  {group.items.map((item) => (
                    <NavItemView
                      key={item.key}
                      item={item}
                      activeHref={activeHref}
                      depth={0}
                      Link={Link}
                      query={q}
                      pinned={pinnedSet}
                      onTogglePin={disablePins ? undefined : togglePin}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
